/**
 * Hub is a service for accessing and configuring hubs.
 * @typedef { Object } Hub
 * @memberof HubService
 */

import { generate } from 'astring'
import * as espree from 'espree'
import { walk } from 'estree-walker'
import { i18n } from 'i18n'
import SwaggerClient from 'swagger-client'
import hubSpec from './swagger/hub.v1.json'
import { Flow, Changelog } from 'services'

class InliningError extends Error {
  constructor({ type, args }, node, code) {
    super(type)
    this.i18n = i18n.t(type, args)
    this.name = 'InliningError'

    // Get relevant lines from flow
    const allLines = _.split(code, '\n')
    const initialLineNo = node.loc.start.line - 1
    const finalLineNo = node.loc.end.line - 1
    var lines = _.filter(allLines, (_, lineNo) => lineNo >= initialLineNo && lineNo <= finalLineNo)

    // Do last line first
    const lastLine = lines[lines.length - 1]
    lines[lines.length - 1] = [
      ...lastLine.slice(0, node.loc.end.column),
      ...'</em>',
      ...lastLine.slice(node.loc.end.column, lastLine.length)
    ]

    // Do first line last
    const firstLine = lines[0]
    lines[0] = [
      ...firstLine.slice(0, node.loc.start.column),
      ...'<em>',
      ...firstLine.slice(node.loc.start.column, firstLine.length)
    ]

    // add some context before and after
    if (initialLineNo > 1) {
      lines.unshift(allLines[initialLineNo - 1])
    }

    if (finalLineNo < allLines.length - 1) {
      lines.push(allLines[finalLineNo + 1])
    }

    this.context = lines
      .map((l, i) => `${initialLineNo + i}: ${l.join ? l.join('') : l}`)
      .join('<br/>')
  }
}

const Check = {
  isMemberAccess: (node, module, method) => {
    return (
      node &&
      node.type === 'MemberExpression' &&
      node.object &&
      node.object.type === 'Identifier' &&
      node.object.name === module &&
      node.property &&
      (!method || (node.property.type === 'Identifier' && node.property.name === method))
    )
  },

  isFieldAccess: node => {
    return Check.isMemberAccess(node, 'Fields')
  },

  isInvocation: (node, module, method) => {
    return node.type === 'CallExpression' && Check.isMemberAccess(node.callee, module, method)
  },

  isFlowRun: node => Check.isInvocation(node, 'Flow', 'run'),
  isFlowInclude: node => Check.isInvocation(node, 'Flow', 'include'),

  isServiceGet: node => Check.isInvocation(node, 'Service', 'get'),

  isTableAccess: node => Check.isInvocation(node, 'Table'),

  isExtension: node => Check.isInvocation(node, 'Extends')
}

//
// Builders
//
const Build = {
  accessMember: (obj, member) => {
    return {
      type: 'MemberExpression',
      computed: true,
      object: { type: 'Identifier', name: obj },
      property: { type: 'Literal', value: member }
    }
  },
  object: obj => {
    // Just let espree work this out
    const ast = espree.parse(`var f = ${JSON.stringify(obj)};`)
    return ast.body[0].declarations[0].init
  }
}

//
// Extensions
//

const literalValue = node => {
  return node && node.type === 'Literal' ? node.value : null
}

const collect = arrayExpr => {
  const arr = []
  if (!arrayExpr) return []
  for (let elm of arrayExpr.elements) {
    if (elm.value) {
      arr.push(elm.value)
    } else {
      const item = {}
      for (let prop of elm.properties) {
        item[prop.key.name] = prop.value.value
      }
      arr.push(item)
    }
  }
  return arr
}

const collectKeys = objExpr => {
  return _.filter(
    _.map(objExpr?.properties || [], p => (p.key?.type === 'Literal' ? p.key.value : false))
  )
}

const Extends = {
  withTable: (node, extensions) => {
    const tableName = literalValue(node.arguments[0])
    if (tableName) {
      extensions.push({ type: 'table', name: tableName, description: '' })
    }
  },

  with: (node, extensions) => {
    switch (node.callee.property.name) {
      case 'withString':
        const stringName = literalValue(node.arguments[0])
        if (stringName) {
          extensions.push({
            type: 'string',
            name: stringName,
            shouldMatch: literalValue(node.arguments[1]),
            description: ''
          })
        }
        break
      case 'withSelection':
        const selectionName = literalValue(node.arguments[0])
        if (selectionName) {
          extensions.push({
            type: 'selection',
            name: selectionName,
            options: collect(node.arguments[1]),
            description: ''
          })
        }
        break
      case 'withFlow':
        const flowName = literalValue(node.arguments[0])
        if (flowName) {
          extensions.push({
            type: 'flow',
            name: flowName,
            description: '',
            inputs: collectKeys(node.arguments[1])
          })
        }
        break
      case 'withField':
        const fieldName = literalValue(node.arguments[0])
        if (fieldName) {
          extensions.push({ type: 'field', name: fieldName, description: '' })
        }
        break
    }
  }
}

const inlineField = (node, fields, inlinedFields, code, appId, flowName) => {
  // TODO optical fields
  //

  // Fields["foo"] and Field.foo but not Fields[foo] (where foo is a var)
  if (
    (node.property.type !== 'Literal' && node.computed) ||
    (node.property.type !== 'Identifier' && !node.computed)
  )
    throw new InliningError(
      { type: 'INL_FIELD_NAME_IS_NO_LITERAL', args: { flowName } },
      node,
      code
    )

  const fieldName = /* Literal */ node.property.value || /* Identifier */ node.property.name

  if (!inlinedFields[fieldName]) {
    const field = _.find(fields, f => f.name === fieldName && f.application === appId)

    if (!field)
      throw new InliningError(
        { type: 'INL_NO_SUCH_FIELD', args: { name: fieldName, flowName } },
        node,
        code
      )

    if (field.alternatives && field.alternatives.length > 0) {
      throw new InliningError(
        { type: 'INL_FIELD_HAS_ALTERNATIVES', args: { flowName } },
        node,
        code
      )
    }

    let constructorArgs =
      field.type === 'image'
        ? [
            Build.object({
              ..._.pick(field, ['image', 'offset']),
              matchConfidence: field.matchconfidence,
              matchOnDesktop: field.matchondesktop
            })
          ]
        : [{ type: 'Literal', value: field.path }]

    inlinedFields[fieldName] = {
      type: 'Property',
      kind: 'init',
      key: {
        type: 'Literal',
        value: fieldName
      },
      value: {
        type: 'NewExpression',
        callee: {
          type: 'Identifier',
          name: 'Field'
        },
        arguments: constructorArgs
      }
    }
  }

  // The access member ast fragment
  return Build.accessMember('InlinedFields', fieldName)
}

const getFlowName = (node, code, flowName) => {
  // Verify that there is at least one argument
  if (!node.arguments || node.arguments.length == 0) {
    throw new InliningError({ type: 'INL_NO_NAME_FOR_FLOW', args: { flowName } }, node, code)
  }

  // Verify that the 1st argument is a literal value
  if (node.arguments[0].type !== 'Literal') {
    throw new InliningError({ type: 'INL_FLOW_NAME_IS_NO_LITERAL', args: { flowName } }, node, code)
  }

  return node.arguments[0].value
}

const inlineFlowInclude = (
  node,
  flows,
  inlinedFlows,
  fields,
  inlinedFields,
  inlinedIncludes,
  extensions,
  code,
  appId,
  hostFlowName
) => {
  const flowName = getFlowName(node, code, hostFlowName)

  if (!inlinedIncludes[flowName]) {
    const flow = _.find(
      flows,
      f =>
        (f.name === flowName ||
          f.id === flowName ||
          (f.typeConfig && f.typeConfig.subject) === flowName) &&
        f.type == 'MODULE'
    )

    if (!flow) {
      throw new InliningError({ type: 'INL_NO_SUCH_FLOW', args: { name: flowName } }, node, code)
    }

    // var exports = {};
    const exportsDecl = {
      type: 'VariableDeclaration',
      declarations: [
        {
          type: 'VariableDeclarator',
          id: {
            type: 'Identifier',
            name: 'exports'
          },
          init: {
            type: 'ObjectExpression',
            properties: []
          }
        }
      ],
      kind: 'var'
    }

    // Body of inlined/included flow needs to be inlined as well
    var { ast, extensions } = inlineAst(
      flowName,
      flow.code,
      appId,
      flows,
      fields,
      inlinedFields,
      inlinedFlows,
      inlinedIncludes,
      extensions,
      {
        excludeInlinedDecls: true
      }
    )

    const flowBody = ast.body

    // return exports;
    const returnExports = {
      type: 'ReturnStatement',
      argument: {
        type: 'Identifier',
        name: 'exports'
      }
    }

    inlinedIncludes[flowName] = {
      type: 'Property',
      kind: 'init',
      key: {
        type: 'Literal',
        value: flowName
      },
      value: {
        type: 'FunctionExpression',
        expression: false,
        generator: false,
        params: [],
        body: {
          type: 'BlockStatement',
          body: [exportsDecl, ...flowBody, returnExports]
        }
      }
    }
  }

  return {
    type: 'CallExpression',
    arguments: [],
    callee: Build.accessMember('FlowIncludes', flowName)
  }
}

const inlineFlowRun = (
  node,
  flows,
  inlinedFlows,
  fields,
  inlinedFields,
  inlinedIncludes,
  extensions,
  code,
  appId,
  inFlow
) => {
  const flowName = getFlowName(node, code, inFlow)

  //
  // Flow arguments
  //

  // Copy arguments verbatim
  const args = node.arguments.length > 1 ? [node.arguments[1]] : []

  //
  // Update the object containing all the inlined flows
  //
  if (!inlinedFlows[flowName]) {
    const flow = _.find(
      flows,
      f =>
        (f.name === flowName ||
          f.id === flowName ||
          (f.typeConfig && f.typeConfig.subject) === flowName) &&
        f.type === 'ACTION' &&
        f.application === appId
    )

    if (!flow) {
      throw new InliningError(
        { type: 'INL_NO_SUCH_FLOW_ON_APP', args: { name: flowName, flowName: inFlow } },
        node,
        code
      )
    }

    // Declare all the defined outputs in the start of the inlined func, init them to null
    const outputDecls = _.map(flow.outputs, output => {
      return {
        type: 'VariableDeclaration',
        declarations: [
          {
            type: 'VariableDeclarator',
            id: {
              type: 'Identifier',
              name: output.name
            },
            init: {
              type: 'Literal',
              value: null
            }
          }
        ],
        kind: 'var'
      }
    })

    // Body of inlined flow needs to be inlined as well
    var { ast, extensions } = inlineAst(
      flowName,
      flow.code,
      appId,
      flows,
      fields,
      inlinedFields,
      inlinedFlows,
      inlinedIncludes,
      extensions,
      {
        excludeInlinedDecls: true
      }
    )

    const flowBody = ast.body

    // The return statement which returns and object keyed by the defined output names
    const returnStatement = {
      type: 'ReturnStatement',
      argument: {
        type: 'ObjectExpression',
        properties: _.map(flow.outputs, output => {
          return {
            type: 'Property',
            key: {
              type: 'Identifier',
              name: output.name
            },
            value: {
              type: 'Identifier',
              name: output.name
            },
            kind: 'init'
          }
        })
      }
    }

    inlinedFlows[flowName] = {
      type: 'Property',
      kind: 'init',
      key: {
        type: 'Literal',
        value: flowName
      },
      value: {
        // function taking Inputs as arg, sets output vars to null -- then body from rec call and return and object with output vars
        type: 'FunctionExpression',
        expression: false,
        generator: false,
        params: [{ type: 'Identifier', name: 'Inputs' }],
        body: {
          type: 'BlockStatement',
          body: [...outputDecls, ...flowBody, returnStatement]
        }
      }
    }
  }

  return {
    type: 'CallExpression',
    arguments: args,
    callee: Build.accessMember('FlowRuns', flowName)
  }
}

const inlineAst = (
  flowName,
  code,
  appId,
  flows,
  fields,
  inlinedFields,
  inlinedFlows,
  inlinedIncludes,
  extensions,
  opts
) => {
  const ast = espree.parse(code, { loc: true })

  console.debug('ast', ast)

  inlinedFields = inlinedFields || {}
  inlinedFlows = inlinedFlows || {}
  inlinedIncludes = inlinedIncludes || {}
  extensions = extensions || []

  walk(ast, {
    enter(node, parent, prop, index) {
      if (Check.isFieldAccess(node)) {
        this.replace(inlineField(node, fields, inlinedFields, code, appId, flowName))
      } else if (Check.isFlowRun(node)) {
        this.replace(
          inlineFlowRun(
            node,
            flows,
            inlinedFlows,
            fields,
            inlinedFields,
            inlinedIncludes,
            extensions,
            code,
            appId,
            flowName
          )
        )
      } else if (Check.isFlowInclude(node)) {
        this.replace(
          inlineFlowInclude(
            node,
            flows,
            inlinedFlows,
            fields,
            inlinedFields,
            inlinedIncludes,
            extensions,
            code,
            appId,
            flowName
          )
        )
      } else if (Check.isServiceGet(node)) {
        throw new InliningError(
          { type: 'INL_SERVICE_GET_CANNOT_BE_INLINED', args: { flowName } },
          node,
          code
        )
      } else if (Check.isTableAccess(node)) {
        Extends.withTable(node, extensions)
      } else if (Check.isExtension(node)) {
        Extends.with(node, extensions)
      }
    }
  })

  // If we're in a recursive call we don't want to include the field and flows decls
  if (opts && opts.excludeInlinedDecls) return { ast, extensions }

  const inlinedFieldsDecl = !_.isEmpty(inlinedFields)
    ? [
        {
          type: 'VariableDeclaration',
          kind: 'var',
          declarations: [
            {
              type: 'VariableDeclarator',
              id: {
                type: 'Identifier',
                name: 'InlinedFields'
              },
              init: {
                type: 'ObjectExpression',
                properties: _.values(inlinedFields)
              }
            }
          ]
        }
      ]
    : []

  const inlinedFlowsDecl = !_.isEmpty(inlinedFlows)
    ? [
        {
          type: 'VariableDeclaration',
          kind: 'var',
          declarations: [
            {
              type: 'VariableDeclarator',
              id: {
                type: 'Identifier',
                name: 'FlowRuns'
              },
              init: {
                type: 'ObjectExpression',
                properties: _.values(inlinedFlows)
              }
            }
          ]
        }
      ]
    : []
  const inlinedIncludesDecl = !_.isEmpty(inlinedIncludes)
    ? [
        {
          type: 'VariableDeclaration',
          kind: 'var',
          declarations: [
            {
              type: 'VariableDeclarator',
              id: {
                type: 'Identifier',
                name: 'FlowIncludes'
              },
              init: {
                type: 'ObjectExpression',
                properties: _.values(inlinedIncludes)
              }
            }
          ]
        }
      ]
    : []

  // insert VariableDeclaration of inlined fields and flows
  ast.body = [...inlinedFieldsDecl, ...inlinedFlowsDecl, ...inlinedIncludesDecl, ...ast.body]

  return { ast, extensions }
}

const inline = (
  flowName,
  code,
  appId,
  flows,
  fields,
  inlinedFields,
  inlinedFlows,
  inlinedIncludes
) => {
  var { ast, extensions } = inlineAst(
    flowName,
    code,
    appId,
    flows,
    fields,
    inlinedFields,
    inlinedFlows,
    inlinedIncludes
  )
  return { code: generate(ast), extensions }
}

/**
 *
 * Configured hubs
 *
 **/

import Typenames from './Typenames'
import { ActionTypes, store } from 'store'

export type Hub = {
  id: string,
  name: string,
  token: string,
  url: string
}

const defaultHub: Hub = {
  id: '',
  name: '',
  token: '',
  url: ''
}

export type PublicationType = {
  id: string,
  name: string,
  icon: string,
  description: string
}

/**
 *
 * Hub typename
 * @private
 * @memberof HubService
 */
const TYPENAME = Typenames.HUB

const attachHub = hub => {
  return item => {
    item.body.hub = hub
    return item
  }
}

const withHeaders = headers => {
  return r => {
    r.headers = { ...r.headers, ...headers }
    return r
  }
}

const connectionFor = async hub => {
  hubSpec.schemes = ['https']
  hubSpec.host = hub.url
  const c = await SwaggerClient({
    spec: hubSpec,
    requestInterceptor: withHeaders({ Authorization: `Bearer ${hub.token}` })
  })
  c.hub = hub
  c.auth = `Bearer ${hub.token}`
  return c
}

const connections = async () => {
  const conns = []

  for (let hub of await list()) {
    conns.push(await connectionFor(hub))
  }

  return conns
}

const isFlowPublication = pub =>
  [FLOW_PUBLICATION, INLINED_FLOW_PUBLICATION].some(s => s.indexOf(pub.type) >= 0)

const download = (conn, meta, version, appId, existingFlow) => {
  // The type of the publication determines the actions taken
  if (isFlowPublication(meta)) {
    return conn.apis.Consumer.Consumer_DownloadVersion({
      origin: meta.origin.relative,
      type: meta.type,
      identifier: meta.identifier,
      version: meta.version
    }).then(response => {
      const p = response.body.item
      // Create/update the flow and attach the meta information to the actual item
      // Parse content
      var content = JSON.parse(p.content)

      var newFlow = {
        ...content,
        id: meta.identifier,
        name: meta.name,
        version: version,
        application: appId,
        publication: meta
      }

      // we'd like to preserve any properties on the existing flow (which is not on the new published flow, e.g. extensions)
      newFlow = _.mergeWith(existingFlow || {}, newFlow, (objValue, srcValue, key) => {
        // The extensions property are treated with special care,
        // here we take all the info from the objValue but allow
        // the `value` property to be overridden by local changes.
        // This ensures that inputs are preserved.
        if (key === 'extensions') {
          return _.values(
            _.mergeWith(
              _.keyBy(objValue, v => `${v.type}.${v.name}`),
              _.keyBy(srcValue, v => `${v.type}.${v.name}`),
              (objKeyValue, srcKeyValue) => ({
                ...objKeyValue,
                ...srcKeyValue,
                value: objKeyValue?.value
              })
            )
          )
        }

        if (!srcValue) {
          return objValue
        }
      })

      return Flow.updateFlow(meta.identifier, newFlow, true).then(({ version }) => {
        // Tag the downloaded flow for easy rollback etc
        Changelog.getLastChange(meta.identifier, 'flow').then(change => {
          Changelog.tagChange(meta.identifier, change.identifier, meta.releaseNote.version, 'flow')
        })
        return { ...newFlow, version }
      })
    })
  }

  throw `Cannot download a ${meta.type} publication`
}

const latest = conn => {
  return conn.apis.Consumer.Consumer_ListLatest()
}

const categories = conn => {
  return conn.apis.Consumer.Consumer_ListCategories()
}

const applications = conn => {
  return conn.apis.Consumer.Consumer_ListApplications()
}

const licenses = conn => {
  return conn.apis.Consumer.Consumer_ListLicenses()
}

const about = conn => {
  return conn.apis.Consumer.Consumer_About().then(attachHub(conn.hub))
}

const attachmentsOf = (conn, item) => {
  return conn.apis.Consumer.Consumer_ListAttachmentsOf({
    origin: item.origin.relative,
    type: item.type,
    identifier: item.identifier
  })
}

const updateMeta = (conn, item) => {
  return conn.apis.Manager.Manager_UpdateMeta({
    type: item.type,
    identifier: item.identifier,
    version: item.version,
    body: { meta: item }
  })
}

const metaFor = (conn, meta, version) => {
  return conn.apis.Consumer.Consumer_MetaFor({
    origin: meta.origin.relative,
    type: meta.type,
    identifier: meta.identifier,
    version: version || meta.version
  })
}

const publish = (conn, meta, content, tagInfo) => {
  const publishPromise = conn.apis.Publisher.Publisher_Publish({
    body: { item: { meta, content } }
  })

  // If we're given info to tag we do it
  // Note: If there are multiple publications from one flow then only the last published for
  // a given version will get to tag with its info.
  if (tagInfo) {
    return publishPromise.then(res => {
      Changelog.tagChange(
        tagInfo.flowId,
        tagInfo.changeId,
        `${meta.name} ${meta.releaseNote.version}`,
        'flow'
      )
      return res
    })
  }

  return publishPromise
}

const unpublish = (conn, item) => {
  return conn.apis.Publisher.Publisher_Unpublish({
    type: item.type,
    identifier: item.identifier,
    version: item.version
  })
}

const attach = (conn, item, f) => {
  const formData = new FormData()

  // Append files
  formData.append('attachment', f, f.name)

  return fetch(`https://${conn.hub.url}/v1/publisher/attach/${item.type}/${item.identifier}`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: conn.auth
    }
  })
    .then(response => {
      // we return the url
      return `${conn.hub.url}/v1/consumer/attach/${item.origin.hub}/${
        item.type
      }/${item.identifier || 'REPLACE_WITH_IDENTIFIER'}/${encodeURIComponent(f.name)}`
    })
    .catch(console.err)
}

const deleteAttachment = (conn, item, filename) => {
  return conn.apis.Publisher.Publisher_DeleteAttachment({
    type: item.type,
    identifier: item.identifier,
    filename
  })
}

/**
 * List all configured hubs.
 * @function
 * @memberof HubService
 *
 * @return { Promise<Array<Hub>> } A list of all configured hubs.
 */
const list = () => {
  return store.dispatch(ActionTypes.GET, TYPENAME).then(hubs => {
    const mapped = _.map(hubs, h => {
      return { ...h }
    })
    return mapped
  })
}

/**
 * Saves a hub.
 * @function
 * @memberof HubService
 *
 * @return { Promise<string> } The id of the added/updated hub.
 */
const save = (id: string, hub: Hub): Promise<PostResponse> => {
  if (!id) return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: hub })
  return store.dispatch(ActionTypes.UPDATE, { typename: TYPENAME, id: id, item: hub })
}

/**
 * Deletes a hub.
 * @function
 * @memberof HubService
 *
 * @return { Promise<any> }
 */
const del = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

const FLOW_PUBLICATION = 'FLOW'
const INLINED_FLOW_PUBLICATION = 'INLINED_FLOW'

const publicationTypes = (): Array<PublicationType> => {
  return [
    {
      id: FLOW_PUBLICATION,
      name: i18n.t('PUBLICATION_TYPE_FLOW_NAME'),
      description: i18n.t('PUBLICATION_TYPE_FLOW_DESC'),
      icon: 'shuffle',
      color: ''
    },
    {
      id: INLINED_FLOW_PUBLICATION,
      name: i18n.t('PUBLICATION_TYPE_INLINED_FLOW_NAME'),
      description: i18n.t('PUBLICATION_TYPE_INLINED_FLOW_DESC'),
      icon: 'shuffle',
      color: 'green'
    }
  ]
}

const self = {
  inline,
  defaultHub,
  connections,
  connectionFor,
  latest,
  metaFor,
  categories,
  applications,
  licenses,
  updateMeta,
  publish,
  unpublish,
  attach,
  deleteAttachment,
  attachmentsOf,
  download,
  about,
  publicationTypes,
  FLOW_PUBLICATION,
  INLINED_FLOW_PUBLICATION,
  list,
  save,
  del
}

export default self
